import { useState } from 'react'
import { LogOut } from 'lucide-react'
import { ROLES } from '@/app/nav'
import { useRole } from '@/app/role'
import { getDensity, setDensity, type Density } from '@/app/density'
import { getTheme, setTheme, type Theme } from '@/app/theme'
import { PageHeader } from '@/app/shell/PageHeader'
import { initialsOf, useAuth } from '@/lib/auth/auth'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { cn } from '@/lib/utils'

function Seg<T extends string>({
  options,
  value,
  onChange,
}: {
  options: [T, string][]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-input">
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={cn(
            'px-3 py-1.5 text-[13px] font-medium',
            v === value ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

export function AccountPage() {
  const { role } = useRole()
  const { signOut, user } = useAuth()
  const info = ROLES.find((r) => r.id === role)!
  const [theme, setThemeState] = useState<Theme>(getTheme())
  const [density, setDensityState] = useState<Density>(getDensity())

  return (
    <>
      <PageHeader title="Account" subtitle={<span>Profile &amp; preferences</span>} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <KLabel className="mb-3">Profile</KLabel>
          <div className="flex items-center gap-3">
            <span className="grid size-12 place-items-center rounded-xl bg-gradient-to-br from-stable to-contained font-display text-lg font-bold text-white">
              {initialsOf(user?.name ?? user?.email)}
            </span>
            <div>
              <div className="font-display text-[16px] font-semibold">{user?.name || user?.email || 'Signed in'}</div>
              <div className="text-[12.5px] text-muted-foreground">{info.name}</div>
              <div className="text-[11.5px] text-faint">{user?.email ?? ''}</div>
            </div>
          </div>
        </Card>

        <Card>
          <KLabel className="mb-3">Preferences</KLabel>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">Theme</span>
              <Seg
                value={theme}
                onChange={(v) => {
                  setTheme(v)
                  setThemeState(v)
                }}
                options={[
                  ['light', 'Light'],
                  ['dark', 'Dark'],
                ]}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">Density</span>
              <Seg
                value={density}
                onChange={(v) => {
                  setDensity(v)
                  setDensityState(v)
                }}
                options={[
                  ['comfortable', 'Comfortable'],
                  ['compact', 'Compact'],
                ]}
              />
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-4 flex items-center justify-between">
        <div>
          <div className="text-[13px] font-medium">Session</div>
          <div className="text-[11.5px] text-faint">Signed in with Microsoft Entra ID.</div>
        </div>
        <Button variant="destructive" size="sm" onClick={signOut}>
          <LogOut /> Sign out
        </Button>
      </Card>
    </>
  )
}
